import { ErrorHandler, Injectable } from '@angular/core';
import { merge } from 'lodash';
import { flatten } from 'flat';
import * as Sentry from '@sentry/angular';

import { sentryConfig, shouldDropSentryEvent } from './sentry-config';
import { Logger, LogLevel } from './logger.service';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  private throttleCount = 0;
  private readonly maxThrottleCount = 10;
  private sentryErrorHandler: Sentry.SentryErrorHandler;

  constructor() {
    this.sentryErrorHandler = Sentry.createErrorHandler({ showDialog: false });

    Sentry.init({
      ...sentryConfig,
      // integrations: (sentryConfig.integrations as []).concat([]),
      beforeBreadcrumb: this.beforeBreadcrumb.bind(this),
      beforeSend: this.beforeSend.bind(this),
      beforeSendTransaction: this.beforeSend.bind(this)
    });

    Logger.outputs.push((source, level, objects) => {
      const levelsMap = {
        [LogLevel.Critical]: 'fatal',
        [LogLevel.Error]: 'error',
        [LogLevel.Warning]: 'warning',
        [LogLevel.Info]: 'info',
        [LogLevel.Debug]: 'debug'
      };

      if (sentryConfig.enabled && (levelsMap as any)[level] && Logger.level !== LogLevel.Debug && (objects || []).length > 0) {
        let featuredLog: any;
        if (typeof objects[0] === 'string' || (objects[0]?.message && objects[0]?.stack)) {
          // either if it's a string or an object from "Error" class
          featuredLog = objects.splice(0, 1)[0];
        }

        // Flattens complex error objects to be able to set each value as "Extra" on Sentry
        let flattenedErrorData = merge.apply(
          this,
          [{ source }].concat(objects).map((obj: any) => flatten(obj || {}))
        );
        flattenedErrorData = Object.keys(flattenedErrorData).reduce(
          (result, key) => Object.assign(result, { ['data.' + key]: typeof flattenedErrorData[key] === 'string' ? flattenedErrorData[key] : JSON.stringify(flattenedErrorData[key]) }),
          {}
        );

        Sentry.withScope(scope => {
          scope.setLevel((levelsMap as any)[Logger.level]);
          scope.setTag('data.source', source);
          Object.keys(flattenedErrorData).forEach(key => scope.setExtra(key, flattenedErrorData[key]));

          if (typeof featuredLog === 'string') {
            Sentry.captureMessage(source ? `[${source}] ${featuredLog}` : featuredLog);
          } else if (typeof featuredLog === 'object' && featuredLog !== null) {
            Sentry.captureException(featuredLog);
          } else {
            Sentry.captureMessage(`Something happened (${(levelsMap as any)[Logger.level]})`);
          }
        });
      }
    });
  }

  handleError(error: any) {
    const errorToSend = (error || {}).originalError || error;
    if (sentryConfig.enabled) {
      this.sentryErrorHandler.handleError(errorToSend);
    } else {
      console.error(errorToSend);
    }
  }

  private beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
    if (shouldDropSentryEvent(event)) {
      return null;
    }

    const dropErrorRateConfig: { [key: string]: number } = {
      '<unknown>': 1,
      'Handled unknown error': 1,
      'Error message not found.': 1,
      '[ErrorHandlerService] unknown': 0.95,
      'Empty response': 0.95
    };
    const dropRate = dropErrorRateConfig[event?.message || event?.exception?.values[0]?.value];

    if (dropRate !== undefined && Math.random() <= dropRate) {
      return null;
    }

    this.throttleCount++;
    return this.throttleCount > this.maxThrottleCount ? null : event;
  }

  private beforeBreadcrumb(breadcrumb: Sentry.Breadcrumb, hint: Sentry.BreadcrumbHint) {
    if (breadcrumb.type === 'http') {
      const forbiddenUrls = ['https://www.google-analytics.com', 'https://cloudflareinsights.com', 'https://www.google.com/ccm', 'https://www.facebook.com'];
      const url = typeof breadcrumb.data?.url === 'string' ? breadcrumb.data.url : '';

      if (url && forbiddenUrls.some(forbiddenUrl => url.startsWith(forbiddenUrl))) {
        return null;
      }
    }

    if (breadcrumb.category === 'xhr') {
      const forbiddenOnXhr = [
        // contexto 'this' dá problema e não envia o XHR, então manter array aqui dentro
        'password',
        'current_password',
        'password_confirmation',
        'current',
        'new_confirmation',
        'token',
        'card_number',
        'cvv',
        'signature_id',
        '"operationName":"Signature"',
        '"operationName":"LinkSignature"',
        '"operationName":"AcceptSignature"'
      ];
      const body = (hint.xhr.__sentry_xhr_v2__.body || '').substr(0, 500);
      const response = (hint.xhr.response || '').substr(0, 500);

      return {
        ...breadcrumb,
        data: {
          ...(breadcrumb.data || {}),
          ...(body && body.match(new RegExp(forbiddenOnXhr.join('|'))) ? { body: '[Filtered]' } : { body }),
          ...(response && response.match(new RegExp(forbiddenOnXhr.join('|'))) ? { response: '[Filtered]' } : { response })
        }
      };
    }

    return breadcrumb;
  }
}
